import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { filter } from 'lodash';
import classnames from 'classnames';
import BarChart from 'js/components/Charts/BarChart';
import { PACK_INNER_GAPS_AFTER } from 'js/components/Charts/bar-layout';
import { Icon, IconStack } from 'js/components';

import {
  faCircle,
  faCheck,
  faComments,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faQuestion,
  faCheck as faCheckSolid,
  faCircle as faCircleSolid,
} from '@fortawesome/pro-solid-svg-icons';

import {
  decorationForFraction,
  decorationStyleForLiveTile,
  normalizedValue,
  sentimentDomain,
} from 'js/utils/sentiment-question';
import {
  ANSWERED_TILE,
  RESPONDED_TILE,
  TILE_PRESENTATION,
  getChoiceTallyData,
  liveTilePresentation,
} from '../../utilities';

export const TILE_MODE = Object.freeze({
  RUN: 'run',
  HOST: 'host',
  REVIEW: 'review',
});

const NONE = 'none';

const NUMERIC_EMPTY = Object.freeze({
  wash: NONE,
  decorator: 'traffic',
  distribution: 'empty',
  notScoredLabel: false,
});

const RUN_UNSCORED_EMPTY = Object.freeze({
  wash: NONE,
  decorator: NONE,
  distribution: 'empty',
  notScoredLabel: false,
});

const RUN_UNSCORED_ANSWERED = Object.freeze({
  wash: 'answered',
  decorator: 'answered-check',
  distribution: NONE,
  notScoredLabel: false,
});

function numericParts({ hasResponses, revealed, decoration }) {
  if (!hasResponses) {
    return NUMERIC_EMPTY;
  }
  if (!revealed) {
    return {
      wash: NONE,
      decorator: 'traffic',
      distribution: 'count',
      notScoredLabel: false,
    };
  }
  return {
    wash: decoration ? 'traffic' : NONE,
    decorator: 'traffic',
    distribution: 'bars',
    notScoredLabel: false,
  };
}

function groupUnscoredParts({ hasResponses, revealed, presentation }) {
  const comment = presentation === TILE_PRESENTATION.COMPLETION;
  if (!hasResponses) {
    return {
      wash: NONE,
      decorator: NONE,
      distribution: 'empty',
      notScoredLabel: true,
    };
  }
  return {
    wash: 'responded',
    decorator: NONE,
    distribution: comment ? 'comment-count' : revealed ? 'bars' : 'count',
    notScoredLabel: true,
  };
}

export function assembleLiveTile({
  mode,
  question,
  hasResponses,
  revealed,
  decoration,
  participantScoped = false,
}) {
  const presentation = liveTilePresentation(question);
  const isNumericGlance =
    presentation === TILE_PRESENTATION.SCORED_DISTRIBUTION;

  if (participantScoped) {
    if (!hasResponses) {
      return {
        presentation,
        wash: NONE,
        decorator: NONE,
        distribution: NONE,
        notScoredLabel: false,
      };
    }
    if (isNumericGlance) {
      return {
        presentation,
        wash: decoration ? 'traffic' : NONE,
        decorator: 'traffic',
        distribution: NONE,
        notScoredLabel: false,
      };
    }
    if (mode === TILE_MODE.RUN) {
      return {
        presentation,
        ...RUN_UNSCORED_ANSWERED,
      };
    }
    return {
      presentation,
      wash: 'responded',
      decorator: NONE,
      distribution: NONE,
      notScoredLabel: false,
    };
  }

  const hostResultsHidden = mode === TILE_MODE.HOST && !revealed;

  if (isNumericGlance || hostResultsHidden) {
    return {
      presentation,
      ...numericParts({ hasResponses, revealed, decoration }),
    };
  }

  if (mode === TILE_MODE.RUN) {
    return {
      presentation,
      ...(hasResponses ? RUN_UNSCORED_ANSWERED : RUN_UNSCORED_EMPTY),
    };
  }

  return {
    presentation,
    ...groupUnscoredParts({ hasResponses, revealed, presentation }),
  };
}

function washVars(wash, decoration) {
  if (wash === 'traffic' && decoration) {
    return {
      '--tile-wash': decoration.wash,
      '--tile-ink': decoration.washInk,
    };
  }
  if (wash === 'answered') {
    return {
      '--tile-wash': ANSWERED_TILE.wash,
      '--tile-ink': ANSWERED_TILE.washInk,
    };
  }
  if (wash === 'responded') {
    return {
      '--tile-wash': RESPONDED_TILE.wash,
      '--tile-ink': RESPONDED_TILE.washInk,
    };
  }
  return undefined;
}

function trafficGlyph(decoration) {
  if (decoration == null) {
    return <Icon icon={faQuestion} transform="shrink-7" />;
  }
  if (decoration.colorOnly) {
    return <Icon icon={faCircleSolid} transform={decoration.transform} />;
  }
  return (
    <Icon icon={decoration.icon} inverse transform={decoration.transform} />
  );
}

function QuestionOverview({
  mode,
  participant,
  question,
  onClick,
  responses,
  yLimit,
  forceShowResponses = true,
}) {
  const presentation = liveTilePresentation(question);
  const participantResponses = participant
    ? filter(responses, (r) => r.participant_id === participant.id)
    : responses;
  const scoredResponses = filter(participantResponses, (r) => !r.is_na);
  const filteredResponses = scoredResponses;

  const domain = useMemo(
    () => sentimentDomain(question?.answers),
    [question?.answers]
  );

  const chartData = useMemo(
    () => getChoiceTallyData(question, filteredResponses),
    [question, filteredResponses]
  );

  const meanFraction = useMemo(() => {
    if (presentation !== TILE_PRESENTATION.SCORED_DISTRIBUTION) {
      return null;
    }

    const fractions = filteredResponses
      .map((r) => normalizedValue(r.value, domain))
      .filter((f) => f != null);

    if (fractions.length === 0) {
      return null;
    }

    return fractions.reduce((sum, f) => sum + f, 0) / fractions.length;
  }, [filteredResponses, domain, presentation]);

  const respondingCount = participantResponses.filter(
    (r) => r && (r.is_na || (r.value != null && r.value !== ''))
  ).length;
  const responseCount =
    presentation === TILE_PRESENTATION.SCORED_DISTRIBUTION
      ? scoredResponses.length
      : respondingCount;
  const hasResponses = responseCount > 0;
  const revealed =
    forceShowResponses ||
    (question.reveal_results_on !== null &&
      question.reveal_results_on <= Date.now());

  const decoration =
    presentation === TILE_PRESENTATION.SCORED_DISTRIBUTION &&
    meanFraction != null &&
    (revealed || Boolean(participant))
      ? decorationForFraction(
          meanFraction,
          question?.answers?.filter((answer) => !answer.is_na).length,
          decorationStyleForLiveTile(question)
        )
      : null;

  const parts = assembleLiveTile({
    mode,
    question,
    hasResponses,
    revealed,
    decoration,
    participantScoped: Boolean(participant),
  });

  return (
    <div
      className={classnames('question-overview', {
        'has-result': parts.wash !== 'none',
        'is-choice-distribution':
          parts.presentation === TILE_PRESENTATION.CHOICE_DISTRIBUTION,
        'is-completion': parts.presentation === TILE_PRESENTATION.COMPLETION,
      })}
      style={washVars(parts.wash, decoration)}
      onClick={onClick}
    >
      <div className="content">
        <h4 className="question-title">{question.text}</h4>
        {parts.distribution === 'empty' ? (
          <span className="no-results-chart">
            No
            <br />
            results
          </span>
        ) : null}
        {parts.distribution === 'count' ? (
          <span className="total-response-count">
            <span className="total-response-count-value">
              {responseCount} <Icon icon={faCheck} />
            </span>
          </span>
        ) : null}
        {parts.distribution === 'comment-count' ? (
          <span className="total-response-count">
            <span className="total-response-count-value">
              {responseCount} <Icon icon={faComments} />
            </span>
          </span>
        ) : null}
        {parts.distribution === 'bars' ? (
          <div className="response-data">
            <span className="response-count">
              {responseCount} <Icon icon="check" />
            </span>
            <BarChart
              key={chartData.length}
              data={chartData}
              title=""
              duration="500"
              yLimit={yLimit}
              maxBarThickness={null}
              packInnerGaps={chartData.length > PACK_INNER_GAPS_AFTER}
            />
          </div>
        ) : null}
        {parts.decorator !== 'none' ? (
          <div className="response-result">
            <IconStack className="result-icon">
              <Icon icon={faCircle} />
              {parts.decorator === 'answered-check' ? (
                <Icon icon={faCheckSolid} inverse transform="shrink-8" />
              ) : (
                trafficGlyph(decoration)
              )}
            </IconStack>
          </div>
        ) : null}
        {parts.notScoredLabel ? (
          <div className="not-scored-label">
            Not
            <br />
            scored
          </div>
        ) : null}
      </div>
    </div>
  );
}

QuestionOverview.propTypes = {
  mode: PropTypes.oneOf(Object.values(TILE_MODE)).isRequired,
  onClick: PropTypes.func,
};

QuestionOverview.defaultProps = {
  question: {},
  responses: [],
  onClick: () => {},
};

export default QuestionOverview;
